import { useDispatch } from "react-redux"
import { IAgTechAppContext, useAppContext } from "../../app/AgTechAppContext"
import { useAgTechAuthContext } from "../../auth/AgTechAuthContext"
import { useAgTechDataContext } from "../common/AgTechDataContext"
import { AgTechApiError, AgTechApiResponseHandlingHelpers } from "../http/AgTechCoreApiRequests"
import { AgTechDataActionExecutionResult } from "./AgTechDataActions"

/// Requests

export type AgTechHttpRequestProps = {
    path: string,
    baseURL?: string
}

export type AgTechHttpRequest<TInputType, TResponseType> = {
    execute: (props: { data: TInputType }) => Promise<AgTechHttpResponse<TResponseType>>
}

export type AgTechHttpResponse<TResponseType> = {
    wasAttempted: boolean,
    errorMessage?: string,
    success?: {
        data: TResponseType
    }
}

/// Building Requests

export type AgTechHttpRequestBuildingProps = {
    path: string,
    baseURL: string,
    onError?: (error: AgTechApiError) => Promise<void>,
    appContext: IAgTechAppContext
    token: string,
    logout: () => Promise<void>
}

export type AgTechHttpRequestConfiguration = AgTechHttpRequestBuildingProps & AgTechApiResponseHandlingHelpers;
export type AgTechHttpRequestConfigurationBuilder = (requestProps: AgTechHttpRequestProps) => AgTechHttpRequestConfiguration;

export const useAgTechHttpRequestConfigurationBuilder = (): AgTechHttpRequestConfigurationBuilder => {
    let appContext = useAppContext();
    let authContext = useAgTechAuthContext();
    let dataContext = useAgTechDataContext();
    let dispatch = useDispatch();
    
    return requestProps => ({
        path: requestProps.path,
        baseURL: requestProps.baseURL ?? dataContext.baseURL,
        token: authContext.user.token,
        logout: authContext.logoutUser,
        appContext: appContext,
        dispatch: dispatch
    })
};


/// Responses

export type AgTechPaginatedApiResponse<TEntity> = {
    items: TEntity[]
}

export class AgTechHttpResponses {
    static Default = <TData, >(data: TData): AgTechHttpResponse<TData> => {
        return {
            wasAttempted: false,
            success: {
                data: data
            }
        }
    }

    static Complete = (): AgTechHttpResponse<any> => {
        return {
            wasAttempted: true,
            success: {
                data: null
            }
        }
    }

    static Success = <TData, >(data: TData): AgTechHttpResponse<TData> => {
        return {
            wasAttempted: true,
            success: {
                data: data
            }
        }
    }

    static Failed = (error: any, data?: any): AgTechHttpResponse<any> => {
        return {
            errorMessage: error?.toString(),
            wasAttempted: true
        }
    }
}

/// Execution

export type AgTechHttpRequestExecutor = {
    executeRequest: <TInputData, TResponseData>(
        request: AgTechHttpRequest<TInputData, TResponseData>,
        inputData: TInputData
    ) => Promise<AgTechHttpResponse<TResponseData>>,
    executeAndConvertRequest: <TInputData, TResponseData, TConvertedData>(props: {
        request: AgTechHttpRequest<any, TResponseData>,
        inputData?: TInputData,
        convert: (responseData: TResponseData) => Promise<TConvertedData>,
    }) => Promise<AgTechHttpResponse<TConvertedData>>
}

export const useAgTechHttpRequestExecutor = (): AgTechHttpRequestExecutor => {
    return {
        executeRequest: async (request, inputData) => await request.execute({ data: inputData }),
        executeAndConvertRequest: async props => {
            let initialResponse = await props.request.execute({ data: props.inputData });

            if (initialResponse.success) {
                let convertedData = await props.convert(initialResponse.success.data);

                return {
                    wasAttempted: true,
                    success: {
                        data: convertedData
                    }
                }
            }
            else {
                return {
                    errorMessage: initialResponse.errorMessage,
                    wasAttempted: true,
                    success: undefined
                };
            }
        }
    }
}

/// Conversions

export type AgTechApiEntityResponse<TEntity> = {
    data: TEntity,
    errorMessage: string[]
}

export const convertHttpResponse = <TEntity, >(response: AgTechHttpResponse<AgTechApiEntityResponse<TEntity>>): AgTechHttpResponse<TEntity> => {
    return response.success ? {
        wasAttempted: true,
        success: {
            data: response.success.data.data
        }
    } : {
        wasAttempted: true,
        success: undefined
    }
}

export const convertFromEntityResponse = <TEntity, >(response: AgTechHttpResponse<AgTechApiEntityResponse<TEntity>>): AgTechDataActionExecutionResult<TEntity> => {
    return response.success ? {
        success: {
            data: response.success.data.data
        }
    } : {
        success: undefined
    }
}